import {
  DrawerBody,
  Flex,
  Stack,
  Text,
  Alert,
  AlertDescription,
  AlertIcon,
} from '@chakra-ui/react';
import { InputFormControl } from 'features/common/components/Form';
import { AddEditScopedContextValues } from 'features/context-ids/hooks/useAddEditScopedContext';
import { EnhancedSitecoreContextResourceType } from 'features/context-ids/types/types';
import { FormikErrors } from 'formik';
import EmptyState from 'features/common/components/EmptyState';
import { DataTable } from 'features/common/components/DataTable';
import { useScopedResourcesColumns } from '../../columns/useScopedResourcesColumns';
import { useMemo } from 'react';

const NAME = 'name';
const SCOPE_ID = 'scopeId';
// eslint-disable-next-line no-empty-function
const onChange = () => {};

export const AddEditScopedContextDrawerBody: React.FC<{
  errors: FormikErrors<AddEditScopedContextValues>;
  values: AddEditScopedContextValues;
  setValues: (values: AddEditScopedContextValues) => any;
  isLoading: boolean;
  isInEditMode: boolean;
  handleChange: (e: React.ChangeEvent<any>) => void;
  scopedContextData: EnhancedSitecoreContextResourceType[];
}> = ({
  errors,
  values,
  setValues,
  handleChange,
  isInEditMode,
  isLoading,
  scopedContextData,
}) => {
  const resourcesByType = useMemo(() => {
    const byType: Record<string, EnhancedSitecoreContextResourceType[]> = {};
    scopedContextData.forEach((resource) => {
      if (!byType[resource.type]) {
        byType[resource.type] = [];
      }
      byType[resource.type].push(resource);
    });
    return byType;
  }, [scopedContextData]);

  const columns = useScopedResourcesColumns({
    setValues,
    values,
    scopedContextData,
  });

  const visibleScopedContextData = useMemo(
    () =>
      scopedContextData
        .filter((resource) => resource.isVisible)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [scopedContextData],
  );

  const formatList = (items: string[]): string => {
    if (items.length === 1) return items[0];
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  };

  const getAlertMessages = () => {
    const selectedIds = values.allowedResourceIds || [];
    const selectedResources = scopedContextData.filter((r) =>
      selectedIds.includes(r.id),
    );

    const hasEdge = selectedResources.some((r) => r.type === 'edge');
    const hasSite = selectedResources.some((r) => r.type === 'site');

    const filesSelected =
      resourcesByType.files?.some((f) => selectedIds.includes(f.id)) || false;
    const formsSelected =
      resourcesByType.forms?.some((f) => selectedIds.includes(f.id)) || false;
    const standalonePersonalizeExists =
      resourcesByType.personalize?.some(
        (p) => p.tenantProductCode === 'Personalize',
      ) || false;
    const standalonePersonalizeSelected =
      standalonePersonalizeExists &&
      (resourcesByType.personalize?.some(
        (p) =>
          selectedIds.includes(p.id) && p.tenantProductCode === 'Personalize',
      ) ||
        false);
    const standaloneCdpExists =
      resourcesByType.personalize?.some((p) => p.tenantProductCode === 'CDP') ||
      false;
    const standaloneCdpSelected =
      standaloneCdpExists &&
      (resourcesByType.personalize?.some(
        (p) => selectedIds.includes(p.id) && p.tenantProductCode === 'CDP',
      ) ||
        false);

    const alerts: Array<{ status: 'info' | 'warning'; message: string }> = [];

    if (hasEdge) {
      const missingEdgeResources: string[] = [];

      if (!filesSelected) {
        missingEdgeResources.push('Files');
      }
      if (standalonePersonalizeExists && !standalonePersonalizeSelected) {
        missingEdgeResources.push('Personalize');
      }
      if (standaloneCdpExists && !standaloneCdpSelected) {
        missingEdgeResources.push('CDP');
      }

      if (missingEdgeResources.length > 0) {
        const verb = missingEdgeResources.length === 1 ? 'has' : 'have';
        alerts.push({
          status: 'warning',
          message: `Edge is selected but ${formatList(
            missingEdgeResources,
          )} ${verb} not been selected. Please check documentation to understand the implications.`,
        });
      } else {
        alerts.push({
          status: 'info',
          message:
            'We added all resources that are needed for server-side context ID to work correctly',
        });
      }
    }

    if (hasSite) {
      const missingSiteResources: string[] = [];

      if (!filesSelected) {
        missingSiteResources.push('Files');
      }
      if (!formsSelected) {
        missingSiteResources.push('Forms');
      }
      if (standalonePersonalizeExists && !standalonePersonalizeSelected) {
        missingSiteResources.push('Personalize');
      }
      if (standaloneCdpExists && !standaloneCdpSelected) {
        missingSiteResources.push('CDP');
      }

      if (missingSiteResources.length > 0) {
        const verb = missingSiteResources.length === 1 ? 'has' : 'have';
        alerts.push({
          status: 'warning',
          message: `Site identifier is selected but ${formatList(
            missingSiteResources,
          )} ${verb} not been selected. Please check documentation to understand the implications.`,
        });
      } else {
        alerts.push({
          status: 'info',
          message:
            'We added all resources that are needed for client-side context ID to work correctly',
        });
      }
    }

    return alerts;
  };

  const alertMessages = getAlertMessages();

  return (
    <DrawerBody>
      <Stack gap={4}>
        <Text>
          {isInEditMode
            ? 'You can edit the name and resource.'
            : 'Select the resources to add to the scoped Context ID'}
        </Text>
        <Flex direction='column'>
          {!isLoading && visibleScopedContextData.length === 0 ? (
            <EmptyState
              text='No resources available'
              dataTestId='no-resources-available'
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                onChange={onChange}
                isLoading={isLoading}
                data={visibleScopedContextData}
                pagination='hidden'
                data-behavior-analytics-feature='scoped context table'
              />

              {alertMessages.length > 0 && (
                <Stack spacing={2}>
                  {alertMessages.map((alert, index) => (
                    <Alert
                      key={index}
                      status={alert.status}
                      data-testid={`resource-selection-alert-${index}`}
                    >
                      <AlertIcon />
                      <AlertDescription>{alert.message}</AlertDescription>
                    </Alert>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Flex>
        <Flex pl='5' direction='column' gap={5}>
          <InputFormControl
            {...{
              isInvalid: Boolean(errors.name),
              label: 'Name (optional)',
              name: NAME,
              value: values.name,
              onChange: handleChange,
              error: errors.name,
              maxW: 'xl',
              placeholder: 'Enter name',
            }}
          />
          {isInEditMode && (
            <InputFormControl
              {...{
                label: 'Scoped Context ID',
                name: SCOPE_ID,
                value: values.scopeId,
                maxW: 'xl',
                isDisabled: true,
              }}
            />
          )}
        </Flex>
      </Stack>
    </DrawerBody>
  );
};
